import { Map, MapControls, useMap } from "@repo/ui/components/map";
import { useEffect, useMemo, useRef } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const CARTO_STYLE = "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json";
const SOURCE_ID = "review-items";
const LAYER_ID = "review-items-layer";

/**
 * Lightweight map view of the current pending queue. Shares selection state
 * with ReviewList — clicking a pin selects the item in the hook, which the
 * detail pane reacts to.
 */
export function ReviewMap({
  items,
  selection,
  onSelect
}) {
  const styles = useMemo(() => ({
    light: CARTO_STYLE
  }), []);
  const initialCenter = useMemo(() => {
    const firstLocated = items.find(i => i.location);
    return firstLocated ? [firstLocated.location.longitude, firstLocated.location.latitude] : [15.985, 60.279];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/_jsxs(Map, {
    styles: styles,
    center: initialCenter,
    zoom: 11,
    children: [/*#__PURE__*/_jsx(MapControls, {
      position: "top-right",
      showZoom: true
    }), /*#__PURE__*/_jsx(ReviewItemsLayer, {
      items: items,
      selection: selection,
      onSelect: onSelect
    })]
  });
}

/** Encode composite (type,id) into a single numeric feature id for setFeatureState. */
function encodeFeatureId(type, id) {
  // place → even, feature → odd; safe up to ~1B ids each.
  return type === "place" ? id * 2 : id * 2 + 1;
}
function ReviewItemsLayer({
  items,
  selection,
  onSelect
}) {
  const {
    map,
    isLoaded
  } = useMap();
  const onSelectRef = useRef(onSelect);
  // Latest-callback ref synced during render on purpose (read only from map event handlers).
  // eslint-disable-next-line react-hooks/refs
  onSelectRef.current = onSelect;
  const hoveredIdRef = useRef(null);
  const geojson = useMemo(() => ({
    type: "FeatureCollection",
    features: items.filter(i => i.location).map(i => ({
      type: "Feature",
      id: encodeFeatureId(i.type, i.id),
      properties: {
        kind: i.type,
        id: i.id,
        title: i.title
      },
      geometry: {
        type: "Point",
        coordinates: [i.location.longitude, i.location.latitude]
      }
    }))
  }), [items]);

  // Add source + layer once map is loaded
  useEffect(() => {
    if (!isLoaded || !map) return;
    map.addSource(SOURCE_ID, {
      type: "geojson",
      data: geojson
    });
    map.addLayer({
      id: LAYER_ID,
      type: "circle",
      source: SOURCE_ID,
      paint: {
        "circle-radius": ["case", ["boolean", ["feature-state", "selected"], false], 11, ["boolean", ["feature-state", "hover"], false], 9, 7],
        "circle-color": ["case", ["==", ["get", "kind"], "place"], "#ef4444", "#f59e0b"],
        "circle-stroke-width": ["case", ["boolean", ["feature-state", "selected"], false], 3, ["boolean", ["feature-state", "hover"], false], 2.5, 2],
        "circle-stroke-color": "#ffffff"
      }
    });
    // Smooth transitions when feature-state changes (selected/hover).
    // Keys valid at runtime; missing from maplibre-gl types.
    map.setPaintProperty(LAYER_ID, "circle-radius-transition", {
      duration: 180,
      delay: 0
    });
    map.setPaintProperty(LAYER_ID, "circle-stroke-width-transition", {
      duration: 180,
      delay: 0
    });
    const onClick = e => {
      const feature = e.features?.[0];
      if (!feature) return;
      const props = feature.properties;
      onSelectRef.current({
        type: props.kind,
        id: Number(props.id)
      });
    };
    const onMouseMove = e => {
      map.getCanvas().style.cursor = "pointer";
      const feature = e.features?.[0];
      const fid = feature?.id;
      if (typeof fid !== "number") return;
      if (hoveredIdRef.current === fid) return;
      if (hoveredIdRef.current != null) {
        map.setFeatureState({
          source: SOURCE_ID,
          id: hoveredIdRef.current
        }, {
          hover: false
        });
      }
      hoveredIdRef.current = fid;
      map.setFeatureState({
        source: SOURCE_ID,
        id: fid
      }, {
        hover: true
      });
    };
    const onMouseLeave = () => {
      map.getCanvas().style.cursor = "";
      if (hoveredIdRef.current != null) {
        map.setFeatureState({
          source: SOURCE_ID,
          id: hoveredIdRef.current
        }, {
          hover: false
        });
        hoveredIdRef.current = null;
      }
    };
    map.on("click", LAYER_ID, onClick);
    map.on("mousemove", LAYER_ID, onMouseMove);
    map.on("mouseleave", LAYER_ID, onMouseLeave);
    return () => {
      map.off("click", LAYER_ID, onClick);
      map.off("mousemove", LAYER_ID, onMouseMove);
      map.off("mouseleave", LAYER_ID, onMouseLeave);
      try {
        if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID);
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
      } catch {
        // ignore
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, map]);

  // Sync data when items change
  useEffect(() => {
    if (!isLoaded || !map) return;
    const src = map.getSource(SOURCE_ID);
    if (src) src.setData(geojson);
  }, [isLoaded, map, geojson]);

  // Sync selection via feature-state (animates radius/stroke transitions)
  const prevSelectedIdRef = useRef(null);
  useEffect(() => {
    if (!isLoaded || !map) return;
    if (!map.getSource(SOURCE_ID)) return;
    if (prevSelectedIdRef.current != null) {
      map.setFeatureState({
        source: SOURCE_ID,
        id: prevSelectedIdRef.current
      }, {
        selected: false
      });
    }
    if (selection) {
      const fid = encodeFeatureId(selection.type, selection.id);
      map.setFeatureState({
        source: SOURCE_ID,
        id: fid
      }, {
        selected: true
      });
      prevSelectedIdRef.current = fid;
    } else {
      prevSelectedIdRef.current = null;
    }
  }, [isLoaded, map, selection]);

  // Pan to selection
  useEffect(() => {
    if (!isLoaded || !map || !selection) return;
    const item = items.find(i => i.type === selection.type && i.id === selection.id);
    if (!item?.location) return;
    map.easeTo({
      center: [item.location.longitude, item.location.latitude],
      duration: 400
    });
  }, [isLoaded, map, selection, items]);
  return null;
}